'use client';

import {
  Button,
  ButtonGroup,
  Card,
  CardAside,
  CardContent,
  CardHeader,
  EmptyState,
  Flex,
  Heading,
  LayoutContent,
} from '@/_components';
import { AdminChatList } from './components/AdminChatList';
import { useContext, useMemo, useState, type FC } from 'react';
import { MassEmailDialog } from './components/MassEmailDialog';
import type { AdminMessagesWrapperProps } from './AdminMessagesWrapper.types';
import { useParams, usePathname } from 'next/navigation';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UserContext } from '@/_contexts/UserProvider';

export const AdminMessagesWrapper: FC<AdminMessagesWrapperProps> = ({
  children,
  isAdvisor,
}) => {
  const { isLoading } = useContext(AdminConversationsContext);
  const { selectedCompany, onToggleAddClient } = useContext(CompanyContext);
  const { isLoading: isUserLoading } = useContext(UserContext);

  const pathname = usePathname();
  const { conversationId } = useParams();

  const [massEmailDialogOpened, setMassEmailDialogOpened] =
    useState<boolean>(false);

  const onToggleMassEmailDialogOpened = () =>
    setMassEmailDialogOpened((current) => !current);

  const isClientAside = pathname.startsWith('/inbox') && !conversationId;
  const isAsideNavigation: boolean = pathname === '/messages' || isClientAside;

  const shouldShowAllConversations: boolean = useMemo(
    () =>
      !isUserLoading &&
      !isLoading &&
      isAdvisor &&
      !selectedCompany?.id &&
      pathname === '/messages',
    [isUserLoading, isLoading, isAdvisor, selectedCompany?.id, pathname],
  );
  const isEmpty: boolean = !isLoading && !children;
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(false);

  const onToggleUnreadFilter = () => setShowUnreadOnly(!showUnreadOnly);

  return (
    <>
      <MassEmailDialog
        isOpen={massEmailDialogOpened}
        onClose={onToggleMassEmailDialogOpened}
      />
      <LayoutContent>
        <Card isRow={true}>
          {isAdvisor && (
            <CardAside isNavigationControl={isAsideNavigation}>
              <Flex isColumn={true}>
                <CardHeader>
                  <Flex isBetween={true} gap={4} padding={4} isYCentered={true}>
                    <Heading level={4}>Messages</Heading>
                    {shouldShowAllConversations && (
                      <ButtonGroup>
                        <Button
                          value="Mass Email"
                          isIconOnly={true}
                          withTooltip={true}
                          iconLeft="email"
                          intent="ghost"
                          size="small"
                          onClick={onToggleMassEmailDialogOpened}
                        />
                        <Button
                          iconLeft="eye"
                          onClick={onToggleUnreadFilter}
                          size="small"
                          isIconOnly={true}
                          withTooltip={true}
                          intent={showUnreadOnly ? 'primary' : 'ghost'}
                          value={showUnreadOnly ? 'Show All' : 'Show Unread'}
                        />
                      </ButtonGroup>
                    )}
                  </Flex>
                </CardHeader>
                <AdminChatList
                  isAdvisor={isAdvisor}
                  shouldShowAllConversations={shouldShowAllConversations}
                  showUnreadOnly={showUnreadOnly}
                />
              </Flex>
            </CardAside>
          )}
          <CardContent isFullHeight={true} withAside={isAdvisor}>
            {isEmpty ? (
              <EmptyState
                description="No available conversations found"
                icon="message"
              >
                <ButtonGroup align="center">
                  <Button
                    value="Add Entity"
                    iconLeft="plus"
                    size="small"
                    intent="secondary"
                    onClick={onToggleAddClient}
                  />
                </ButtonGroup>
              </EmptyState>
            ) : (
              children
            )}
          </CardContent>
        </Card>
      </LayoutContent>
    </>
  );
};
